import type { PricingTrustItem } from "@/components/landing/pricing-section/PricingTrustRow";
import type { PricingVariant } from "@/components/landing/pricing-section/PricingFeatureList";

export type BillingCycle = "semester" | "yearly";

export const CURRENCY = "MAD";

export const PRICING_PAGE_HERO_COPY = {
  badge: "SIMPLE PRICING",
  headline: "Simple pricing for Moroccan English teachers.",
  subheading:
    "Start free for 7 days. Choose the plan that fits how often you prepare lessons. No credit card required.",
  noCardLine: "No credit card required.",
  trustChips: [
    "7-day free trial",
    "Local MAD pricing",
    "Moroccan bank transfer",
    "No automatic renewal",
  ],
} as const;

export const BILLING_CYCLE_OPTIONS = [
  {
    value: "semester",
    label: "Semester",
    subtext: "Pay per semester",
  },
  {
    value: "yearly",
    label: "Yearly",
    subtext: "One annual payment",
  },
] satisfies readonly {
  value: BillingCycle;
  label: string;
  subtext: string;
}[];

export const SUBSCRIPTION_PLAN_COPY = [
  {
    id: "freeTrial",
    label: "FREE TRIAL",
    variant: "purple",
    tagline: "Try Darsi with full access.",
    features: [
      "Lesson plans",
      "Quizzes & tests",
      "Audio generation",
      "Word export",
      "No credit card",
    ],
    cta: { label: "Start free", href: "/sign-up" },
  },
  {
    id: "starter",
    label: "STARTER",
    variant: "orange",
    tagline: "Unlimited lesson plans for everyday prep.",
    features: [
      "Unlimited lesson plans",
      "Word & print export",
      "Teacher & school branding",
      "History of your lessons",
      "PAYG lesson credits stay separate",
    ],
    cta: { label: "Choose Starter", href: "/buy?section=plans&focus=starter&entry=public_pricing" },
  },
  {
    id: "pro",
    label: "PRO",
    variant: "green",
    tagline: "The full workspace for lesson prep.",
    features: [
      "Unlimited lesson plans",
      "Quizzes & tests",
      "Audio generation",
      "Templates (your style)",
      "New Pro features",
    ],
    cta: { label: "Choose Pro", href: "/buy?section=plans&focus=pro&entry=public_pricing" },
    badge: "Complete workspace",
  },
] satisfies readonly {
  id: "freeTrial" | "starter" | "pro";
  label: string;
  variant: PricingVariant;
  tagline: string;
  features: readonly string[];
  cta: { label: string; href: string };
  badge?: string;
}[];

export const PAYG_CARD_COPY = {
  label: "PAYG CREDITS",
  tagline: "Buy only what you need.",
  priceCaption: "per accepted lesson",
  sliderLabel: "Lesson credits",
  features: [
    "Pay only for accepted lessons",
    "Previews do not use credits",
    "Credits do not expire",
    "Lesson planning only",
    "Good for occasional use",
  ],
  cta: { label: "Buy credits", href: "/buy?section=credits&entry=public_pricing" },
} as const;

export const CREDITS_BANNER_COPY = {
  heading: "Prefer credits?",
  bodyPrefix: "Pay as you go. Buy",
  bodySuffix: "lesson credits and pay only for accepted lesson plans.",
  priceCaption: "per accepted lesson",
} as const;

export const PRICING_TRUST_ITEMS = [
  {
    icon: "card-off",
    tone: "purple",
    title: "No credit card required",
    subtitle: "Start without payment details",
  },
  {
    icon: "mad",
    iconLabel: "MAD",
    tone: "purple",
    title: "Local MAD pricing",
    subtitle: "Made for Moroccan teachers",
  },
  {
    icon: "bank",
    tone: "green",
    title: "Moroccan bank transfer",
    subtitle: "Attijariwafa & CIH supported",
  },
  {
    icon: "shield",
    tone: "blue",
    title: "No automatic renewal",
    subtitle: "You approve every payment",
  },
] satisfies readonly PricingTrustItem[];

export const PRICING_FAQ_ITEMS = [
  {
    id: 1,
    question: "Do I need a credit card?",
    answer: "No. Darsi uses local MAD pricing and Moroccan bank transfer.",
    defaultExpanded: true,
  },
  {
    id: 2,
    question: "Is the trial really free?",
    answer: "Yes. Start with a 7-day free trial - no credit card required.",
    defaultExpanded: false,
  },
  {
    id: 3,
    question: "Can I switch later?",
    answer: "Yes. Starter can upgrade to Pro after approval. An active Pro plan cannot schedule a Starter downgrade; choose Starter after Pro expires.",
    defaultExpanded: false,
  },
  {
    id: 4,
    question: "Who is Pro for?",
    answer:
      "Teachers who want lesson plans, tests, audio, and templates in one workspace.",
    defaultExpanded: false,
  },
] as const;

export const MID_PAGE_CTA_COPY = {
  heading: "Start free and see if Darsi fits your classroom.",
  subtext: "No credit card required. Local MAD pricing.",
  primary: { label: "Start free for 7 days", href: "/sign-up" },
  signedInSecondary: { label: "Go to dashboard", href: "/dashboard" },
  signedOutSecondary: { label: "Sign in", href: "/sign-in" },
} as const;

export function formatMadAmount(amount: number): string {
  return Number.isInteger(amount)
    ? amount.toString()
    : amount.toFixed(1).replace(/\.0$/, "");
}

export function billingCyclePeriod(cycle: BillingCycle): string {
  return cycle === "semester" ? "/ semester" : "/ year";
}

export function inactiveBillingCycle(cycle: BillingCycle): BillingCycle {
  return cycle === "semester" ? "yearly" : "semester";
}

export function billingCyclePriceLabel(cycle: BillingCycle): string {
  return cycle === "semester" ? "semester" : "year";
}
